import dayjs from "dayjs";
import { DefaultValues } from "react-hook-form";
import { z } from "zod";
import { LineSchema } from "@components/Dashboard/pages/Sales/Orders/components/MutationLayout/types";

export const addProductFormScheme = z
    .object({
        id: z.number().nullish(),
        add_more: z.boolean(),
        comment: z.string().nullish(),
        product: z.object({
            id: z.number(),
            name: z.string(),
            code: z.string(),
            barcode: z.string().nullish(),
            in_stock: z.number().nullish(),
            prices: z.object({
                selling_price: z.number(),
            }),
            category: z
                .object({
                    id: z.number(),
                    name: z.string(),
                    code: z.string().nullish(),
                })
                .nullish(),
            has_bom: z.boolean(),
            is_service: z.boolean(),
        }),
        quantity: z.coerce.number().positive(),
        reserved_quantity: z.coerce.number().min(0).optional(),
        unit_price: z.coerce.number().min(0).nullish(),
        discount: z.coerce.number().min(0).max(100).optional(),
        tax: z
            .object({
                id: z.number(),
                name: z.string().nullish(),
                rate: z.number(),
            })
            .nullish(),
        availableQuantity: z.number().nullish(),
        should_use_bom: z.boolean(),
        selected_bom: z
            .object({
                id: z.number(),
                name: z.string(),
                components: z.array(
                    z.object({
                        product: z.object({
                            id: z.number(),
                            name: z.string(),
                            code: z.string(),
                            in_stock: z.number().nullish(),
                            prices: z.object({
                                selling_price: z.number(),
                            }),
                            is_service: z.boolean(),
                        }),
                        quantity: z.coerce.number(),
                    })
                ),
            })
            .optional(),
        // TODO: Alternative name is only available for twenty_four_seven_office for now, but may be extended in the future
        alternative_name: z.string().nullish(),
    })
    .superRefine((scheme, ctx) => {
        if (scheme.should_use_bom && !scheme.selected_bom) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                params: {
                    i18n: "sales.general.modals.addProduct.bom.validation.noBOMSelected",
                },
                path: ["selected_bom"],
            });
        }

        if (scheme.reserved_quantity !== undefined && scheme.reserved_quantity > scheme.quantity) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Reserved quantity cannot exceed quantity",
                path: ["reserved_quantity"],
            });
        }

        return scheme;
    });

export type AddProductFormSchemeTyping = z.infer<typeof addProductFormScheme>;
export type TaxOption = NonNullable<AddProductFormSchemeTyping["tax"]>;
export type DraftMode = "create" | "edit";

export interface DiscountOption {
    id: string;
    group?: string;
    name?: string;
    discount: number | undefined;
    salePrice?: number;
    calculated_price: number | undefined;
    min_quantity?: number;
    valid_from?: dayjs.Dayjs;
    valid_to?: dayjs.Dayjs | null;
}

export interface CreateCarryOver {
    add_more: boolean;
    taxId?: number;
}

export type DraftFinishResult =
    | {
          action: "close";
      }
    | {
          action: "continue";
          carryOver: CreateCarryOver;
      };

export type AddProductLineMutationPayload = AddProductFormSchemeTyping & {
    in_stock?: number | null;
    min_sale_quantity?: number | null;
};

export const DEFAULT_CREATE_CARRY_OVER: CreateCarryOver = {
    add_more: false,
};

export function getTaxStorageKey(companyId?: number) {
    return `so_last_selected_tax:${companyId}`;
}

export function getResetValues(
    overrides: DefaultValues<AddProductFormSchemeTyping> = {}
): DefaultValues<AddProductFormSchemeTyping> {
    // Treating each modal record as a full draft object instead of trying to patch a subset of
    // fields. A brand-new draft is the safest reset boundary for the add-more workflow.
    return {
        id: undefined,
        add_more: false,
        comment: undefined,
        product: undefined,
        quantity: undefined,
        reserved_quantity: undefined,
        unit_price: undefined,
        discount: undefined,
        tax: undefined,
        availableQuantity: undefined,
        should_use_bom: false,
        selected_bom: undefined,
        alternative_name: null,
        ...overrides,
    };
}

export function buildCreateDraftValues(
    carryOver: CreateCarryOver = DEFAULT_CREATE_CARRY_OVER
): DefaultValues<AddProductFormSchemeTyping> {
    // The create draft only carries over flags that are intentionally shared between records.
    // Tax is restored separately once RTKQ provides the current tax options for this draft.
    return getResetValues({
        add_more: carryOver.add_more,
    });
}

export function buildEditDraftValues(
    selectedLine: LineSchema
): DefaultValues<AddProductFormSchemeTyping> {
    // Editing mode is just another draft session with a different seed. Using the same draft
    // shape for create and edit keeps every field on one consistent contract.
    return getResetValues({
        id: selectedLine.id,
        add_more: false,
        comment: selectedLine.comment,
        product: selectedLine.product,
        quantity: selectedLine.quantity,
        unit_price: selectedLine.unit_price,
        discount: selectedLine.discount,
        tax: selectedLine.tax,
        availableQuantity: selectedLine.availableQuantity,
        alternative_name: selectedLine.alternative_name ?? null,
    });
}

export function getRememberedTaxId(storageKey: string): number | undefined {
    const rawTax = localStorage.getItem(storageKey);
    if (!rawTax) return undefined;

    try {
        const storedTax = JSON.parse(rawTax) as { id?: number };
        return storedTax.id;
    } catch {
        return undefined;
    }
}

export function getTaxOptionById(
    taxesOptions: TaxOption[] | undefined,
    taxId?: number
) {
    if (!taxesOptions?.length || !taxId) return undefined;
    return taxesOptions.find((tax) => tax.id === taxId);
}
